import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useMeters, useTimeSeries } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
  ChartTooltip,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

const METRICS = [
  { value: "kw", label: "Active Power", unit: "W" },
  { value: "kwh", label: "Energy (kWh)", unit: "kWh" },
  { value: "voltage", label: "Voltage", unit: "V" },
  { value: "current", label: "Current", unit: "A" },
  { value: "power_factor", label: "Power Factor", unit: "" },
  { value: "frequency", label: "Frequency", unit: "Hz" },
];

export default function TimeSeriesPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];

  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [metric, setMetric] = useState("kw");
  const [days, setDays] = useState(90);

  const { data, isLoading } = useTimeSeries(meterId || meters[0], metric, days);

  const unit = METRICS.find((m) => m.value === metric)?.unit ?? "";
  const chartData =
    data?.data.map((d) => ({
      ts: fmt.dt(d.timestamp),
      value: d.value,
    })) ?? [];

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Consumption Timeline"
          subtitle="Interactive time-series for any electrical parameter"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <select
            className="ra-select-sm"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
          >
            {METRICS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <DaysSelect value={days} onChange={setDays} />
        </PageHeader>

        <ChartCard
          title={METRICS.find((m) => m.value === metric)?.label ?? metric}
          subtitle={`Meter: ${meterId} · Last ${days} days`}
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="ts"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<ChartTooltip unit={unit} />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={metric}
                  stroke={CHART_COLORS.blue}
                  dot={false}
                  strokeWidth={2}
                  activeDot={{ r: 4, fill: CHART_COLORS.blue }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </Layout>
  );
}
